import type { FieldMetadata, MergeRecord } from '../../data/client';
import { useUnmerge, useUnmergePreflight } from '../../data/hooks';
import { Button, Dialog, Icon, Skeleton, useToast } from '../../ui';

/**
 * UNDOING A MERGE (identity-resolution.md 4.7), admin only.
 *
 * TWO CALLS, and the first one is the point. The preflight is not a loading step: it
 * is the honest list of what an unmerge CANNOT do, and an operator who is not shown it
 * will believe the record came back whole. Unmerge is a repair, not time travel:
 *
 *  - fields written AFTER the merge keep their current value, because nothing can know
 *    which of the two humans that value belongs to;
 *  - linked records created after the merge stay on the surviving record, named here
 *    with counts, for the same reason;
 *  - above the journal cap the merge is marked partial and some links cannot go back
 *    at all.
 *
 * The confirm token comes from the preflight the operator was just shown, which is the
 * INV-8 contract: proof that a human was shown THIS plan, not merely that a human
 * clicked.
 */
export interface UnmergeDialogProps {
  readonly merge: MergeRecord;
  readonly fields: readonly FieldMetadata[];
  readonly onClose: () => void;
}

export function UnmergeDialog({ merge, fields, onClose }: UnmergeDialogProps) {
  const preflight = useUnmergePreflight(merge.id);
  const unmerge = useUnmerge();
  const toast = useToast();

  const plan = preflight.data;
  const labelOf = (fieldId: string): string =>
    fields.find((field) => field.id === fieldId)?.label ?? fieldId;
  const blocked = plan === undefined || plan.refusal !== null;

  return (
    <Dialog
      alert
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Undo merge"
      description="The archived record comes back. What happened after the merge does not."
      footer={
        <div className="flex flex-col-reverse gap-2 md:flex-row md:justify-end">
          <Button variant="ghost" className="w-full md:w-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            className="w-full md:w-auto"
            disabled={blocked}
            loading={unmerge.isPending}
            onClick={() => {
              if (plan === undefined) return;
              unmerge.mutate(
                { mergeId: merge.id, confirmToken: plan.confirmToken },
                {
                  onSuccess: (result) => {
                    toast({
                      title: 'Merge undone',
                      description:
                        result.keptFieldIds.length === 0
                          ? 'Both records are live again and every field the merge changed was put back.'
                          : `Both records are live again. ${result.keptFieldIds.length} ${result.keptFieldIds.length === 1 ? 'field written after the merge keeps its' : 'fields written after the merge keep their'} current value.`,
                      tone: 'neutral',
                    });
                    onClose();
                  },
                  onError: (error) =>
                    toast({
                      title: 'Unable to undo the merge.',
                      description: error.message,
                      tone: 'danger',
                    }),
                },
              );
            }}
          >
            Undo merge
          </Button>
        </div>
      }
    >
      {preflight.isPending ? (
        <div className="space-y-2" aria-busy="true">
          <Skeleton className="h-4 w-56" label="Checking what can be undone" />
          <Skeleton className="h-4 w-64" />
        </div>
      ) : preflight.isError ? (
        <p className="text-sm text-danger">{preflight.error.message}</p>
      ) : plan === undefined ? null : (
        <div className="space-y-4">
          {plan.refusal !== null && (
            <p className="flex items-start gap-2 rounded-control border border-danger bg-danger-subtle px-3 py-2 text-sm text-danger">
              <Icon name="blocked" size="1rem" className="mt-0.5 shrink-0" />
              <span>{plan.refusal}</span>
            </p>
          )}

          <section>
            <h3 className="text-2xs font-semibold text-ink-muted">What comes back</h3>
            <ul className="mt-1 space-y-1 text-sm text-ink">
              <li>The archived record is live again and gets its identifiers back.</li>
              <li>
                {plan.revertableFieldIds.length === 0
                  ? 'No field on the surviving record has to change.'
                  : `${plan.revertableFieldIds.map(labelOf).join(', ')} go back to the value held before the merge.`}
              </li>
              <li>The two records are recorded as different people and stop being proposed.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-2xs font-semibold text-ink-muted">What does not</h3>
            <ul className="mt-1 space-y-1 text-sm text-ink">
              {plan.changedSinceFieldIds.length > 0 && (
                <li>
                  <span className="font-medium">
                    {plan.changedSinceFieldIds.map(labelOf).join(', ')}
                  </span>{' '}
                  {plan.changedSinceFieldIds.length === 1 ? 'was' : 'were'} written after the merge
                  and {plan.changedSinceFieldIds.length === 1 ? 'keeps' : 'keep'} the current value.
                  Nothing can know which of the two people it belongs to.
                </li>
              )}
              {plan.strandedRelations.map((entry) => (
                <li key={`${entry.objectLabel}-${entry.fieldLabel}`}>
                  {entry.count} {entry.objectLabel.toLowerCase()} ({entry.fieldLabel}) were linked
                  after the merge and stay on the surviving record.
                </li>
              ))}
              {plan.unmergePartial && (
                <li className="text-warning">
                  This merge moved more linked records than could be recorded, so some of them
                  cannot be moved back at all.
                </li>
              )}
              {plan.changedSinceFieldIds.length === 0 &&
                plan.strandedRelations.length === 0 &&
                !plan.unmergePartial && (
                  <li className="text-ink-muted">
                    Nothing was written since the merge, so this undo is complete.
                  </li>
                )}
            </ul>
          </section>
        </div>
      )}
    </Dialog>
  );
}
